import { Form, Formik } from 'formik';
import React, { useEffect, useRef, useState } from 'react'
import "./medicine-sig-form.scss"
import { addDoctorMedicine, createDoctorMedicine, updateDoctorMedicine } from '../../../../../service/api/doctor';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronUp, faTrash } from '@fortawesome/free-solid-svg-icons';
import { useDispatch, useSelector } from 'react-redux';
import { getDoctorMedicineList } from '../../../../../store/slices/prescriptionSlice';
import { medicineUpdate } from '../../../../../service/api/patient-api';
import { string } from "yup";
import { del, post } from '../../../../../utils/fetchAPI';
import { prescriptionUpdateSubDocument } from '../../../../../service/api/prescription';
import { Toast } from '../../../../../utils/SwalUti';
import { templateUpdateSubDocument } from '../../../../../service/api/template';
import { setAuthInfo } from '../../../../../store/slices/authSlice';
import { AutoComplete } from 'antd';
import SigFields from './sig-fields';
import { getGenericSigs, searchGenerics } from '../../../../../service/api/generic-sig';
import {
    buildDuration,
    composeSig,
    formatDuration,
    genericSigToInfo,
    getGenericSigSuggestion,
    getSigTypeWord,
    isWeightDosed,
    normGeneric,
    parseDuration,
} from '../../../../../utils/common-funtions/medicine-sig';

const MedicineSigForm = ({
    setPrescribedMedicines,
    setPrescriptionMedicineToState,
    visitId,
    updateState,
    isDocMedicineUp = false,
    setIsDocMedicineUp,
    isDoubleClick = false,
    setIsDoubleClick,
    setDocMedicineShow,
    setSearchString,
    searchString = "",
    doctorId,
    medicineAddToPrescription,
    setShow,
    selectedMedicine,
    templateId,
    fieldKey = 'medicines',
    onWideChange,
    preloadedSigs,
}) => {
    const { user } = useSelector((state) => state.auth);
    const [language, setLanguage] = useState(user?.user?.details?.config?.language || "english")

    // Picked from the global medicine list (master docs carry no `doctor`;
    // My Drug List rows do), as opposed to editing a saved/prescribed one.
    const isGlobalPick = !!selectedMedicine?._id && !selectedMedicine?.doctor && !isDocMedicineUp && !isDoubleClick
    // The generic's saved SIGs, loaded by the parent BEFORE the modal opened so the
    // first paint is the final one (no jump from narrow/empty to wide/filled).
    // null = not provided for this medicine -> loaded here after mount instead.
    const preloaded = preloadedSigs && normGeneric(preloadedSigs.generic) === normGeneric(selectedMedicine?.generic)
        ? preloadedSigs
        : null
    // a global pick opens already filled with the saved SIG that is safe to use
    const initialSig = isGlobalPick ? preloaded?.autoFill || null : null

    const [info, setInfo] = useState({
        name: selectedMedicine?.name || searchString || "",
        take: selectedMedicine?.take || "",
        route: selectedMedicine?.route || "",
        frequency: selectedMedicine?.frequency || "",
        instruction: selectedMedicine?.instruction || "",
        repeat: selectedMedicine?.repeat?.split("/")[0] || "1",
        repeat_time: selectedMedicine?.repeat?.split("/")[1]?.replace("s", "") || "day",
        ...parseDuration(selectedMedicine?.duration),
        power: selectedMedicine?.power || "",
        type: selectedMedicine?.type || "",
        generic: selectedMedicine?.generic || "",
        manufacturer: selectedMedicine?.manufacturer || "",
        ...(initialSig ? genericSigToInfo(initialSig) : {}),
        addToDoctorMedicine: false
    });

    const [isSubmitting, setIsSubmitting] = useState(false)
    const [sig, setSig] = useState(initialSig?.sig || selectedMedicine?.sig || "")
    const [isMedicineSave, setIsMedicineSave] = useState(false)
    const [showMore, setShowMore] = useState({ generic: 45, power: 45 })
    // const [repeatTime, setRepeatTime] = useState(info?.repeat?.split(" ")[1] || "day(s)")
    // const [durationTime, setDurationTime] = useState(info?.duration?.split("/")[1] || "day(s)")
    const [enableDelete, setEnableDelete] = useState(false)
    // The doctor's saved SIGs for the form's generic (right-hand list), best first
    const [genericSigs, setGenericSigs] = useState(preloaded?.presets || [])
    const [genericSigsFor, setGenericSigsFor] = useState(preloaded?.presets?.length ? preloaded.generic : "")
    const [appliedGenericSigId, setAppliedGenericSigId] = useState(initialSig?._id || null)
    const [autoFilled, setAutoFilled] = useState(!!initialSig)
    const [showAllSigs, setShowAllSigs] = useState(false)
    const [genericOptions, setGenericOptions] = useState([])
    const genericSearchTimeoutRef = useRef(null)
    const genericSigRequestRef = useRef(0)
    const latestGenericRef = useRef(selectedMedicine?.generic || "")
    const dispatch = useDispatch()

    const hasSigPanel = genericSigs.length > 0
    const visibleSigs = showAllSigs ? genericSigs : genericSigs.slice(0, 5)

    // auto = filled without the doctor choosing it -> marked until they edit
    const applyGenericSig = (preset, { auto = false } = {}) => {
        setInfo((prev) => ({ ...prev, ...genericSigToInfo(preset) }))
        setSig(preset?.sig || "")
        setAppliedGenericSigId(preset?._id || null)
        setAutoFilled(auto)
    }

    // Load the saved SIGs for a generic; autoApply fills the form with the one that
    // is safe to use without asking (same form/strength/language, not weight-dosed).
    const loadGenericSigs = (generic, medicine, { autoApply = false } = {}) => {
        const requestId = ++genericSigRequestRef.current
        if (!generic?.trim() || !doctorId) return
        getGenericSigs(doctorId, { generic: generic.trim() })
            .then((res) => {
                // a newer request, or the generic was changed meanwhile
                if (requestId !== genericSigRequestRef.current || res?.status !== 200) return
                if (normGeneric(latestGenericRef.current) !== normGeneric(generic)) return
                const lang = user?.user?.details?.config?.language || "english"
                const { presets, autoFill } = getGenericSigSuggestion(res?.data || [], medicine, lang)
                setGenericSigs(presets)
                setGenericSigsFor(generic.trim())
                setShowAllSigs(false)
                if (autoApply && autoFill) applyGenericSig(autoFill, { auto: true })
            })
            .catch((error) => {
                console.log(error);
            })
    }

    const clearGenericSigs = () => {
        setGenericSigs([])
        setGenericSigsFor("")
        setAppliedGenericSigId(null)
        setAutoFilled(false)
    }

    const dosingIsEmpty = () =>
        !info?.take && !info?.route && !info?.frequency && !info?.instruction && !info?.duration && !sig?.trim()

    // Generic chosen in the Create / edit-drug header
    const pickGeneric = (generic) => {
        latestGenericRef.current = generic
        loadGenericSigs(generic, { type: info?.type, power: info?.power }, { autoApply: dosingIsEmpty() })
    }

    const handleGenericChange = (text) => {
        latestGenericRef.current = text
        setInfoFun("generic", text);
        searchGenericOptions(text);
        if (genericSigsFor && normGeneric(text) !== normGeneric(genericSigsFor)) clearGenericSigs()
    }

    const searchGenericOptions = (text) => {
        clearTimeout(genericSearchTimeoutRef.current)
        if (!text?.trim()) {
            setGenericOptions([])
            return
        }
        genericSearchTimeoutRef.current = setTimeout(() => {
            searchGenerics(text.trim())
                .then((res) => {
                    if (res?.status === 200) setGenericOptions(res?.data || [])
                })
                .catch((error) => {
                    console.log(error);
                })
        }, 400)
    }

    const onSubmit = (values) => {
        values.name = info?.name
        values.take = info?.take || ""
        values.route = info?.route || ""
        values.frequency = info?.frequency || ""
        values.instruction = info?.instruction || ""
        values.sig = sig || ""

        values.repeat = info?.repeat + "/" + ((info?.repeat > 1 || info?.repeat > "১") ? (info?.repeat_time + "s") : info?.repeat_time)
        // values.duration = info?.duration + "/" + ((info?.duration > 1 || info?.duration > "১") ? (info?.duration?.length > 0 ? (info.duration_time + "s") : "") : (info?.duration?.length > 0 ? info.duration_time : ""))
        values.duration = buildDuration(info)
        values.power = info?.power
        values.type = info?.type
        values.generic = info?.generic
        values.manufacturer = info?.manufacturer
        values.addToDoctorMedicine = info?.addToDoctorMedicine

        if (selectedMedicine?._id && !isDocMedicineUp && !isDoubleClick && !isMedicineSave) {
            values.medicineId = selectedMedicine?._id
            medicineAddToPrescription(values)
            setDocMedicineShow(true)
            setShow(false)
            setIsDoubleClick(false)
            setSearchString("")
        } else if (!isDocMedicineUp && !isDoubleClick) {
            if (selectedMedicine?.doctorMedicineId) {
                values.doctorMedicineId = selectedMedicine?.doctorMedicineId
            }
            // Global pick saved to my drug list: keep the link to the master medicine
            if (isGlobalPick) {
                values.medicine = selectedMedicine?._id
                values.medicineId = selectedMedicine?._id
            }
            const { addToDoctorMedicine, ...rest } = values
            values = rest
            createDoctorMedicine(doctorId, values)
                .then((res) => {
                    if (res?.status === 200) {

                        values.doctorMedicineId = res?.data?._id
                        medicineAddToPrescription(values)
                    }
                })
                .catch((error) => {
                    console.log(error);
                })
                .finally(() => {
                    setDocMedicineShow(true)
                    setShow(false)
                    setIsDoubleClick(false)
                    setSearchString("")
                }
                );
        } else if (isDocMedicineUp && !isDoubleClick) {
            const { addToDoctorMedicine, doctorId, ...rest } = values
            values = rest
            updateDoctorMedicine(selectedMedicine?._id, values)
                .then((res) => {
                    if (res?.status === 200) {
                        const payload = { doctorId: user?.user?._id, search: "" }
                        dispatch(getDoctorMedicineList(payload))
                    }
                })
                .catch((error) => {
                    console.log(error);
                })
                .finally(() => {
                    setDocMedicineShow(true)
                    setShow(false)
                    setIsDoubleClick(false)
                    setSearchString("")
                    setIsDocMedicineUp(false)
                }
                );
        } else if (isDoubleClick && !isDocMedicineUp) {
            values.prescribedMedicineId = selectedMedicine?._id
            if (selectedMedicine?.doctorMedicine?.length > 0) values.doctorMedicineId = selectedMedicine?.doctorMedicine
            updateMedicine(selectedMedicine?._id, values, visitId)
        }


    }

    const durationFormatFun = (duration) => {
        let ary = duration?.split('/')
        return ary[0] || ""
    }

    const updateMedicine = async (medicineId, values, visitId) => {

        let payload = {}
        payload["_id"] = medicineId || ""
        payload[`${fieldKey}.$.name`] = values?.name || ""
        payload[`${fieldKey}.$.take`] = values?.take || ""
        payload[`${fieldKey}.$.frequency`] = values?.frequency || ""
        payload[`${fieldKey}.$.instruction`] = values?.instruction || ""
        payload[`${fieldKey}.$.sig`] = values?.sig || ""
        payload[`${fieldKey}.$.repeat`] = values?.repeat || ""
        payload[`${fieldKey}.$.duration`] = values?.duration || ""
        payload[`${fieldKey}.$.power`] = values?.power || ""
        payload[`${fieldKey}.$.type`] = values?.type || ""
        payload[`${fieldKey}.$.generic`] = values?.generic || ""
        payload[`${fieldKey}.$.manufacturer`] = values?.manufacturer || ""

        if (visitId) {
            await prescriptionUpdateSubDocument(fieldKey, payload, visitId).then((res) => {
                if (res.status == 200) {
                    setPrescribedMedicines(res?.data?.[fieldKey])
                    setPrescriptionMedicineToState(res?.data?.[fieldKey])
                    updateState(res?.data)
                    setDocMedicineShow(true)
                    setShow(false)
                    setIsDoubleClick(false)
                    setSearchString("")
                    setIsDocMedicineUp(false)
                    setEnableDelete(false)
                    const payload = { doctorId: user?.user?._id, search: "" }
                    dispatch(getDoctorMedicineList(payload))
                }
            })
                .catch((error) => {
                    console.log(error);
                })
                .finally()
        }
        else if (templateId) {
            await templateUpdateSubDocument(fieldKey, payload, templateId).then((res) => {
                if (res.status == 200) {
                    setPrescribedMedicines(res?.data?.[fieldKey])
                    setPrescriptionMedicineToState(res?.data?.[fieldKey])
                    setDocMedicineShow(true)
                    setIsDoubleClick(false)
                    setSearchString("")
                    setIsDocMedicineUp(false)
                    setEnableDelete(false)
                    const payload = { doctorId: user?.user?._id, search: "" }
                    dispatch(getDoctorMedicineList(payload))
                    setShow(false)
                }
            })
                .catch((error) => {
                    console.log(error);
                })
                .finally()
        }

    }


    const setInfoFun = (key, value) => {
        let temp = { ...info };
        if (["take", "route", "frequency", "instruction", "duration", "duration_time"].includes(key)) setAutoFilled(false)

        if (value == "চলবে" || value == "continue") {
            setInfo({ ...temp, [key]: value, duration: value });
        }
        else {
            setInfo({ ...temp, [key]: value });
        }
        if (key == "repeat" || key == "route" || key == "frequency" || key == "take" || key == "repeat_time" || key == "instruction") {
            let sig = ''
            const take = key == "take" ? value : info?.take
            const route = key == "route" ? value : info?.route
            const frequency = key == "frequency" ? value : info?.frequency
            const repeat = key == "repeat" ? value : info?.repeat
            const r_time = key == "repeat_time" ? value : info?.repeat_time
            const instruction = key == "instruction" ? value : info?.instruction

            const type = getSigTypeWord(info?.type || selectedMedicine?.type, language)

            // sig = (take ? `${take} ${type}` : "")
            // + (route ? " " + `${route}` : "")
            // + (frequency ? " " + `(${frequency})` : "")
            // + (repeat ? " " + `${(repeat == 1 || repeat == '১') ?  "everyday" : `every ${repeat} ${(repeat == 1 || repeat == '১') ? r_time : r_time + "s"}`}` : "")

            sig = composeSig({ take, typeWord: type, route, frequency, instruction })
            setSig(sig)
        }
    };


    const showMoreFun = (key, value) => {
        let temp = { ...showMore }
        setShowMore({ ...temp, [key]: value })
    }

    useEffect(() => {
        if (!selectedMedicine?.name && !selectedMedicine?.medicineName) setInfoFun("medicineName", searchString);
        if (user?.user?.details?.config?.language)
            setLanguage(user?.user?.details?.config?.language)
    }, [])

    // Saved SIGs for the medicine's generic: a global pick may be pre-filled with a
    // safe one; editing a saved / prescribed medicine only lists them. Normally the
    // parent has preloaded them (see `preloaded`); this is the fallback.
    useEffect(() => {
        if (preloaded || !selectedMedicine?.generic) return
        if (isGlobalPick) loadGenericSigs(selectedMedicine.generic, selectedMedicine, { autoApply: true })
        else if (isDocMedicineUp || isDoubleClick) loadGenericSigs(selectedMedicine.generic, selectedMedicine)
    }, [])

    // Let the parent widen the modal while the SIG list is shown
    useEffect(() => {
        onWideChange?.(hasSigPanel)
    }, [hasSigPanel])
    useEffect(() => () => onWideChange?.(false), [])

    const medicineDelete = async () => {
        await del(`doctor/delete-new-medicine/${selectedMedicine?._id}`)
            .then((res) => {
                if (res.status == 200) {
                    setDocMedicineShow(true)
                    setShow(false)
                    setIsDoubleClick(false)
                    setSearchString("")
                    setIsDocMedicineUp(false)
                    const payload = { doctorId: user?.user?._id, search: "" }
                    dispatch(getDoctorMedicineList(payload))
                }
            })
            .catch((error) => {
                console.log(error);
            })
            .finally()
    }



    const handleLanguage = async (checked) => {
        let language = "english"
        if (checked) language = 'bangla'
        else language = "english"
        let userInfo = JSON.parse(localStorage.getItem('user'))
        let key = `details.config.language`
        setLanguage(language)
        await post('user/update', { [key]: language })
            .then((res) => {
                if (res.status === 200) {
                    dispatch(setAuthInfo({ token: userInfo?.token, user: res?.data }))
                }
            })
            .catch((error) => {
                console.log(error);
            });
    }
    return (
        <div className={`medicine-sig-layout ${hasSigPanel ? "has-sig-panel" : ""}`}>
            <div className="medicine-sig-layout__form">
            <Formik
                initialValues={{}}
                onSubmit={onSubmit}
            >
                {(formik) => (
                    <div className="medicine-sig-from-section">
                        <div className='d-flex justify-content-between '>
                            <label className='mt-1'>Details</label>
                            <div className='d-flex'>
                                <p className=""><input checked={user?.user?.details?.config?.language == "bangla" ? true : false} className="" onChange={(e) => {
                                    handleLanguage(e.target.checked)
                                }} name="check-2" id="check-2" type="checkbox"></input></p>
                                <label htmlFor="check-2" className="mt-1" style={{ cursor: "pointer" }}>Bangla</label>
                            </div>
                        </div>
                        {(selectedMedicine?.name && (!isDocMedicineUp || selectedMedicine?.medicine)) ? <div className=" header mb-0 pb-0 pt-1 px-0 pb-2">
                            <p className='m-0 p-0'><span>Medicine name: </span><input onChange={(e) => {
                                setInfoFun("name", e.target.value);
                            }} value={info?.name}></input></p>
                            <p className='m-0 p-0'><span>Manufacturer: </span>{selectedMedicine?.manufacturer}</p>

                            <p className='m-0 p-0'><span>Medicine type: </span>{selectedMedicine?.type}</p>
                            {selectedMedicine?.power?.length > 45 ?
                                <p className='m-0 p-0' title={selectedMedicine?.power}><span>Power: </span>{selectedMedicine?.power?.slice(0, Number(showMore?.power))} <span
                                    className='cursor ' onClick={() => {
                                        showMoreFun("power", showMore?.power == selectedMedicine?.power?.length ? 45 : selectedMedicine?.power?.length)
                                    }}>{showMore?.power == selectedMedicine?.power?.length ? " show less" : "...show more"} <FontAwesomeIcon id={`social-history-icon`} size="xs"
                                        className={`mt-1 cursor ${showMore?.power == selectedMedicine?.power?.length ? "rotate-180" : ""}`}
                                        icon={faChevronDown} /></span></p> :
                                <p className='m-0 p-0' title={selectedMedicine?.power}><span>Power: </span>{selectedMedicine?.power}</p>}

                            {selectedMedicine?.generic?.length > 45 ?
                                <p className='m-0 p-0' title={selectedMedicine?.generic}><span>Generic: </span>{selectedMedicine?.generic?.slice(0, Number(showMore?.generic))}
                                    <span className='cursor ' onClick={() => {
                                        showMoreFun("generic", showMore?.generic == selectedMedicine?.generic?.length ? 45 : selectedMedicine?.generic?.length)
                                    }}>{showMore?.generic == selectedMedicine?.generic?.length ? " show less" : "...show more"} <FontAwesomeIcon id={`social-history-icon`}
                                        size="xs"
                                        className={`mt-1 cursor ${showMore?.generic == selectedMedicine?.generic?.length ? "rotate-180" : ""}`}
                                        icon={faChevronDown} /></span></p> :
                                <p className='m-0 p-0' title={selectedMedicine?.generic}><span>Generic: </span>{selectedMedicine?.generic}</p>}
                        </div> :
                            <div className='header mb-0 pb-0 pt-1 px-0 pb-2'>
                                <div className="col-12 m-0 p-0 individual-input d-flex">
                                    <label htmlFor="exampleInputEmail1" className="form-label mt-1">Name:</label>
                                    <input className="form-control input w-50" autoComplete="off" type="text"
                                        value={info?.name || ""} placeholder="name" onChange={(e) => {
                                            setInfoFun("name", e.target.value);
                                        }}
                                    />
                                </div>
                                <div className="col-12 m-0 p-0 individual-input d-flex">
                                    <label htmlFor="exampleInputEmail1" className="form-label mt-1">Power:</label>
                                    <input className="form-control input w-50" autoComplete="off" type="text"
                                        value={info?.power || ""} placeholder="power" onChange={(e) => {
                                            setInfoFun("power", e.target.value);
                                        }}
                                    />
                                </div>
                                <div className="col-12 m-0 p-0 individual-input d-flex">
                                    <label htmlFor="exampleInputEmail1" className="form-label mt-1">Type:</label>
                                    <input className="form-control input w-50" autoComplete="off" type="text"
                                        value={info?.type || ""} placeholder="type" onChange={(e) => {
                                            setInfoFun("type", e.target.value);
                                        }}
                                    />
                                </div>
                                <div className="col-12 m-0 p-0 individual-input d-flex">
                                    <label htmlFor="exampleInputEmail1" className="form-label mt-1">Generic:</label>
                                    {/* suggests generics from the master medicine list; custom text still allowed */}
                                    <AutoComplete
                                        className="w-50 generic-autocomplete"
                                        options={genericOptions.map((generic) => ({ value: generic }))}
                                        value={info?.generic || ""}
                                        placeholder="Generic"
                                        onChange={handleGenericChange}
                                        onSelect={(value) => {
                                            setInfoFun("generic", value);
                                            pickGeneric(value);
                                        }}
                                        onBlur={() => {
                                            // typed out in full instead of picked
                                            const match = genericOptions.find((generic) => normGeneric(generic) === normGeneric(info?.generic))
                                            if (match && normGeneric(match) !== normGeneric(genericSigsFor)) pickGeneric(match)
                                        }}
                                    />
                                </div>
                                <div className="col-12 m-0 p-0 individual-input d-flex">
                                    <label htmlFor="exampleInputEmail1" className="form-label mt-1">Manufacturer:</label>
                                    <input className="form-control input w-50" autoComplete="off" type="text"
                                        value={info?.manufacturer || ""} placeholder="Manufacturer" onChange={(e) => {
                                            setInfoFun("manufacturer", e.target.value);
                                        }}
                                    />
                                </div>
                            </div>}
                        <Form
                            action=""
                            id=""
                            className="custom-form"
                            encType="multipart/form-data"
                        >
                            <div className="row  border-top pt-2 px-2">
                                {/* <p className='p-0 my-0'>Drug details</p> */}
                                {autoFilled && <div className="col-12 sig-autofill-banner">
                                    Filled from your saved SIG for {genericSigsFor} — check before saving.
                                </div>}
                                <SigFields
                                    info={info}
                                    sig={sig}
                                    language={language}
                                    highlight={autoFilled}
                                    onFieldChange={setInfoFun}
                                    onSigChange={(value) => {
                                        setSig(value);
                                        setAutoFilled(false);
                                    }}
                                    onDurationPick={(option) => {
                                        setInfo({ ...info, duration: option.count, duration_time: option.unit });
                                        setAutoFilled(false);
                                    }}
                                />
                                {!isDocMedicineUp && !isDoubleClick && <div className={`save-medicine d-flex w-100 mx-0 px-0`}>
                                    <input onChange={() => {
                                        setIsMedicineSave(!isMedicineSave)
                                        setInfoFun("addToDoctorMedicine", !info?.addToDoctorMedicine);
                                    }} className='mx-0 px-0' type="checkbox" id='save-doc-medicine' name="save-doc-medicine" checked={isMedicineSave} />
                                    <label className='ml-0 align-content-center justify-content-center my-auto' htmlFor="save-doc-medicine">Add {info?.name || "this medicine"} to My Drug List</label><br />
                                </div>}
                                <div className="mx-auto text-center mt-2 d-flex mb-2">
                                    <button
                                        type="submit"
                                        className="btn save-button d-flex mr-4 w-100 "
                                    >
                                        <span>Save</span>
                                        {isSubmitting && (
                                            <span
                                                className="spinner-border spinner-border-sm "
                                                role="status"
                                                aria-hidden="true"
                                            ></span>
                                        )}
                                    </button>
                                    <p

                                        className="btn close-button mx-auto w-100"
                                        onClick={() => {
                                            setShow(false)
                                            setIsDoubleClick(false)
                                            setIsDocMedicineUp(false)
                                        }}
                                    >
                                        Close
                                    </p>
                                </div>
                            </div>
                        </Form>
                    </div>
                )}
            </Formik>
            <div className="row appointment-details pb-3 mt-2">
                <p className="col-12 px-3 py-0 my-0 py-2 d-flex"><input checked={enableDelete || ""} className="my-0 py-0 " onChange={() => {
                    setEnableDelete(!enableDelete)
                }} name="check-1" id="check-1" type="checkbox"></input> <label htmlFor="check-1" className="px-1 py-0 my-0 d-inline" style={{ cursor: "pointer" }}>Delete this
                    medicine</label></p>
                <p className="col-6 px-3 py-0 my-0 ">  <span onClick={() => {
                    medicineDelete()
                }} className={`icon ${enableDelete ? "enable" : "disable"} btn btn-danger btn-sm d-inline p-1 px-3 my-0`}>Delete <FontAwesomeIcon icon={faTrash}></FontAwesomeIcon></span>
                </p>
            </div>
            </div>
            {hasSigPanel && <aside className="medicine-sig-layout__sigs">
                <div className="sig-list__head">
                    <span>Saved SIGs</span>
                    <small title={genericSigsFor}>{genericSigsFor}</small>
                </div>
                {isWeightDosed(info?.type) && <div className="sig-list__note">
                    Dosed by weight/age — pick a SIG and check the dose.
                </div>}
                <div className="sig-list">
                    {visibleSigs.map((preset) => (
                        <button
                            type="button"
                            key={preset?._id}
                            className={`sig-list__item ${appliedGenericSigId === preset?._id ? "is-active" : ""}`}
                            onClick={() => applyGenericSig(preset)}
                        >
                            <span className="sig-list__meta">
                                <span className="sig-list__badge" title={`${preset?.form || "Any form"}${preset?.power ? ` · ${preset.power}` : ""}`}>{preset?.form || "Any form"}{preset?.power ? ` · ${preset.power}` : ""}</span>
                                {preset?.language === "bangla" && <span className="sig-list__tag">বাংলা</span>}
                                {preset?.usageCount > 0 && <span className="sig-list__usage">used {preset.usageCount}×</span>}
                            </span>
                            <span className="sig-list__sig">{preset?.sig}</span>
                            {preset?.duration && <span className="sig-list__duration">{formatDuration(preset.duration)}</span>}
                        </button>
                    ))}
                </div>
                {genericSigs.length > visibleSigs.length && (
                    <button type="button" className="sig-list__more" onClick={() => setShowAllSigs(true)}>
                        Show all ({genericSigs.length})
                    </button>
                )}
            </aside>}
        </div>
    )
}

export default MedicineSigForm


